// frontend/src/pages/ProjectDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import { 
    ArrowLeftIcon, 
    PlusIcon, 
    CheckCircleIcon,
    ChatBubbleLeftIcon,
    DocumentTextIcon,
    UserGroupIcon
} from '@heroicons/react/24/outline';

const ProjectDetail = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [project, setProject] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [newComment, setNewComment] = useState('');
    const [showMilestoneModal, setShowMilestoneModal] = useState(false);
    const [showDeliverableModal, setShowDeliverableModal] = useState(false);

    const [milestoneForm, setMilestoneForm] = useState({ name: '', description: '', due_date: '' });
    const [deliverableForm, setDeliverableForm] = useState({ name: '', description: '', type: 'document', file_url: '' });

    useEffect(() => {
        loadProjectDetails();
    }, [projectId]);

    const loadProjectDetails = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/projects/${projectId}`);
            setProject(response.data.data);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load project details');
            navigate('/projects');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateMilestone = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`/projects/${projectId}/milestone`, milestoneForm);
            toast.success('Milestone created');
            setShowMilestoneModal(false);
            setMilestoneForm({ name: '', description: '', due_date: '' });
            loadProjectDetails();
        } catch (error) {
            toast.error('Failed to create milestone');
        }
    };

    const handleUpdateMilestoneStatus = async (milestoneId, status) => {
        try {
            await axios.put(`/projects/milestone/${milestoneId}`, { status });
            toast.success('Milestone status updated');
            loadProjectDetails();
        } catch (error) {
            toast.error('Failed to update milestone');
        }
    };

    const handleSubmitDeliverable = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`/projects/${projectId}/deliverable`, deliverableForm);
            toast.success('Deliverable submitted');
            setShowDeliverableModal(false);
            setDeliverableForm({ name: '', description: '', type: 'document', file_url: '' });
            loadProjectDetails();
        } catch (error) {
            toast.error('Failed to submit deliverable');
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;
        try {
            await axios.post(`/projects/${projectId}/comment`, { comment: newComment });
            setNewComment('');
            loadProjectDetails();
        } catch (error) {
            toast.error('Failed to add comment');
        }
    };

    const handleCompleteProject = async () => {
        if (!window.confirm('Mark this project as completed?')) return;
        try {
            await axios.post(`/projects/${projectId}/complete`);
            toast.success('Project completed! 🎉');
            loadProjectDetails();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to complete project');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-600">Loading project...</div>
            </div>
        );
    }

    if (!project) return null;

    return (
        <div className="w-full space-y-6">
            <Link to="/projects" className="inline-flex items-center text-sm text-gray-600 hover:text-black space-x-1">
                <ArrowLeftIcon className="w-4 h-4" />
                <span>Back to Projects</span>
            </Link>

            {/* Top Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                <div className="flex justify-between items-start flex-wrap gap-4">
                    <div>
                        <div className="flex items-center space-x-3">
                            <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
                            <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 capitalize">
                                {project.status}
                            </span>
                        </div>
                        <p className="text-gray-600 mt-1 text-sm">{project.description}</p>
                    </div>

                    <div className="flex space-x-2">
                        {project.canComplete && project.status !== 'completed' && (
                            <button
                                onClick={handleCompleteProject}
                                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1"
                            >
                                <CheckCircleIcon className="w-5 h-5" />
                                <span>Complete Project</span>
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm border-t border-gray-100 pt-4">
                    <div>
                        <span className="text-gray-500 block text-xs">Type</span>
                        <span className="font-medium capitalize">{project.type?.replace('_', ' ')}</span>
                    </div>
                    <div>
                        <span className="text-gray-500 block text-xs">Priority</span>
                        <span className="font-medium capitalize">{project.priority || 'Medium'}</span>
                    </div>
                    <div>
                        <span className="text-gray-500 block text-xs">Logged Hours</span>
                        <span className="font-medium">{project.hours?.total || 0} hrs</span>
                    </div>
                    <div>
                        <span className="text-gray-500 block text-xs">Progress</span>
                        <span className="font-medium">{project.progress || 0}%</span>
                    </div>
                </div>

                <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                    <div 
                        className="bg-black h-full transition-all duration-500" 
                        style={{ width: `${project.progress || 0}%` }}
                    />
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex space-x-4 border-b border-gray-200">
                {['overview', 'milestones', 'deliverables', 'team', 'comments'].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`pb-2 px-1 text-sm font-medium capitalize border-b-2 transition-colors ${
                            activeTab === tab 
                                ? 'border-black text-black' 
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* Tab Contents */}
            {activeTab === 'overview' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    {project.objective && (
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">Objective</h3>
                            <p className="text-sm text-gray-600 mt-1">{project.objective}</p>
                        </div>
                    )}

                    {project.scope && (
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">Scope</h3>
                            <p className="text-sm text-gray-600 mt-1">{project.scope}</p>
                        </div>
                    )}

                    {project.business && (
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">Business Partner</h3>
                            <p className="text-sm text-gray-600 mt-1">
                                {project.business.name} ({project.business.industry})
                            </p>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'milestones' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">Milestones</h3>
                        <button
                            onClick={() => setShowMilestoneModal(true)}
                            className="px-3 py-1.5 bg-black text-white text-xs font-medium rounded hover:bg-gray-800 flex items-center space-x-1"
                        >
                            <PlusIcon className="w-4 h-4" />
                            <span>Add Milestone</span>
                        </button>
                    </div>

                    {project.milestones?.length === 0 ? (
                        <p className="text-sm text-gray-500 py-4">No milestones defined yet.</p>
                    ) : (
                        <div className="space-y-3">
                            {project.milestones?.map((m) => (
                                <div key={m.id} className="border border-gray-200 rounded-lg p-4 flex justify-between items-center">
                                    <div>
                                        <h4 className="font-semibold text-sm text-gray-900">{m.name}</h4>
                                        <p className="text-xs text-gray-500 mt-0.5">{m.description}</p>
                                        {m.due_date && <p className="text-xs text-gray-400 mt-1">Due: {m.due_date}</p>}
                                    </div>
                                    <select
                                        value={m.status}
                                        onChange={(e) => handleUpdateMilestoneStatus(m.id, e.target.value)}
                                        className="text-xs border border-gray-300 rounded p-1 bg-white"
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="in_progress">In Progress</option>
                                        <option value="completed">Completed</option>
                                        <option value="delayed">Delayed</option>
                                    </select>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'deliverables' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">Deliverables</h3>
                        <button
                            onClick={() => setShowDeliverableModal(true)}
                            className="px-3 py-1.5 bg-black text-white text-xs font-medium rounded hover:bg-gray-800 flex items-center space-x-1"
                        >
                            <PlusIcon className="w-4 h-4" />
                            <span>Submit Deliverable</span>
                        </button>
                    </div>

                    {/* Show deliverables */}
                    <div className="space-y-3">
                        {project.milestones?.flatMap(m => m.deliverables || []).length === 0 ? (
                            <p className="text-sm text-gray-500 py-4">No deliverables submitted yet.</p>
                        ) : (
                            project.milestones?.flatMap(m => m.deliverables || []).map(d => (
                                <div key={d.id} className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h4 className="font-semibold text-sm text-gray-900">{d.name}</h4>
                                            <p className="text-xs text-gray-500 mt-0.5">{d.description}</p>
                                        </div>
                                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100 font-medium capitalize">
                                            {d.status}
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'team' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
                    {project.members?.length === 0 ? (
                        <p className="text-sm text-gray-500">No members listed</p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {project.members?.map(m => (
                                <div key={m.id} className="p-3 border border-gray-200 rounded-lg flex items-center space-x-3">
                                    <UserGroupIcon className="w-6 h-6 text-gray-400" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">
                                            {m.intern?.User?.first_name} {m.intern?.User?.last_name}
                                        </p>
                                        <p className="text-xs text-gray-500">{m.role || 'Member'} {m.is_lead ? '(Lead)' : ''}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'comments' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Discussion</h3>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                        {project.comments?.map(c => (
                            <div key={c.id} className="text-sm border-b border-gray-100 pb-2">
                                <p className="text-gray-800">{c.comment}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                    {c.User?.first_name} {c.User?.last_name} • {new Date(c.created_at).toLocaleDateString()}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="flex space-x-2 pt-2">
                        <input
                            type="text"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Add a comment..."
                            className="flex-1 text-sm border border-gray-300 rounded p-2 focus:outline-none focus:ring-1 focus:ring-black"
                            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                        />
                        <button
                            onClick={handleAddComment}
                            className="px-4 py-2 bg-black text-white text-sm font-medium rounded hover:bg-gray-800"
                        >
                            Post
                        </button>
                    </div>
                </div>
            )}

            {/* Add Milestone Modal */}
            {showMilestoneModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <form onSubmit={handleCreateMilestone} className="bg-white rounded-lg p-6 max-w-md w-full space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Add Milestone</h3>
                        <input
                            type="text"
                            required
                            placeholder="Milestone Name"
                            value={milestoneForm.name}
                            onChange={(e) => setMilestoneForm({ ...milestoneForm, name: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                        />
                        <textarea
                            placeholder="Description"
                            value={milestoneForm.description}
                            onChange={(e) => setMilestoneForm({ ...milestoneForm, description: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                            rows={2}
                        />
                        <input
                            type="date"
                            value={milestoneForm.due_date}
                            onChange={(e) => setMilestoneForm({ ...milestoneForm, due_date: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                        />
                        <div className="flex justify-end space-x-2">
                            <button type="button" onClick={() => setShowMilestoneModal(false)} className="px-3 py-1.5 text-xs text-gray-600">Cancel</button>
                            <button type="submit" className="px-3 py-1.5 bg-black text-white text-xs rounded hover:bg-gray-800">Create</button>
                        </div>
                    </form>
                </div>
            )}

            {/* Submit Deliverable Modal */}
            {showDeliverableModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <form onSubmit={handleSubmitDeliverable} className="bg-white rounded-lg p-6 max-w-md w-full space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Submit Deliverable</h3>
                        <input
                            type="text"
                            required
                            placeholder="Deliverable Name"
                            value={deliverableForm.name}
                            onChange={(e) => setDeliverableForm({ ...deliverableForm, name: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                        />
                        <textarea
                            placeholder="Description"
                            value={deliverableForm.description}
                            onChange={(e) => setDeliverableForm({ ...deliverableForm, description: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                            rows={2}
                        />
                        <input
                            type="url"
                            placeholder="File URL / Link"
                            value={deliverableForm.file_url}
                            onChange={(e) => setDeliverableForm({ ...deliverableForm, file_url: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                        />
                        <div className="flex justify-end space-x-2">
                            <button type="button" onClick={() => setShowDeliverableModal(false)} className="px-3 py-1.5 text-xs text-gray-600">Cancel</button>
                            <button type="submit" className="px-3 py-1.5 bg-black text-white text-xs rounded hover:bg-gray-800">Submit</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default ProjectDetail;
